import { useState } from "react";
import { AI_NOTICE, type AiPublicResult, type MarkType } from "@xinghai/shared";
import { Card, Tag } from "@xinghai/ui";
import { MARK_LABEL } from "../annotator/labels";

const STATUS_LABEL: Record<AiPublicResult["status"], string> = {
  queued: "排队中",
  running: "评分中",
  done: "已评分",
  failed: "失败",
  skipped: "已跳过",
};

const MARK_TONE: Record<MarkType, string> = {
  correct: "xz-ai__char--ok",
  accented: "xz-ai__char--warn",
  substitution: "xz-ai__char--bad",
  deletion: "xz-ai__char--bad",
  insertion: "xz-ai__char--bad",
  unrecognizable: "xz-ai__char--bad",
};

function percent(value: number): string {
  return `${Math.round(value * 100)}`;
}

/**
 * AI v1 card. Renders nothing when the server omitted the result (flag off,
 * or this caller is not allowed to see it).
 */
export function AiScoreCard({ ai, text }: { ai?: AiPublicResult | null; text?: string | null }) {
  if (!ai) return null;
  const glyphs = [...(text ?? "")];
  return (
    <Card padding="md" className="xz-ai">
      <div className="xz-ai__head">
        <Tag
          tone={ai.status === "done" ? "success" : ai.status === "failed" ? "danger" : "warning"}
        >
          AI v1评分
        </Tag>
        <span className="gz-caption">{STATUS_LABEL[ai.status]}</span>
        {ai.status === "done" && ai.overall !== null && (
          <span className="xz-ai__overall">总分 {percent(ai.overall)}</span>
        )}
      </div>

      {ai.status === "done" && (
        <>
          {ai.dims.length > 0 && (
            <ul className="xz-ai__dims">
              {ai.dims.map((dim) => (
                <li key={dim.key}>
                  <span>{dim.label}</span>
                  <span>{dim.anchor}</span>
                </li>
              ))}
            </ul>
          )}
          {ai.chars.length > 0 && (
            <div className="xz-ai__chars" role="list">
              {ai.chars.map((ch) => (
                <CharMark
                  key={ch.char_index}
                  glyph={ch.char || glyphs[ch.char_index] || ""}
                  mark={ch.mark_type}
                  note={ch.note}
                />
              ))}
            </div>
          )}
          {ai.summary && <p className="xz-ai__summary">{ai.summary}</p>}
        </>
      )}

      {ai.error && <p className="gz-caption xz-ai__error">{ai.error}</p>}

      <p className="xz-ai__notice">{AI_NOTICE}</p>
      {ai.model && <p className="gz-caption xz-ai__model">{ai.model}</p>}
    </Card>
  );
}

function CharMark({ glyph, mark, note }: { glyph: string; mark: MarkType; note: string }) {
  const [open, setOpen] = useState(false);
  const label = `${glyph} ${MARK_LABEL[mark]}${note ? `：${note}` : ""}`;
  return (
    <button
      type="button"
      role="listitem"
      className={`xz-ai__char ${MARK_TONE[mark]}`}
      aria-label={label}
      aria-expanded={note ? open : undefined}
      title={note || undefined}
      onClick={() => note && setOpen((v) => !v)}
    >
      <span className="xz-ai__glyph">{glyph}</span>
      {note && open && <span className="xz-ai__note">{note}</span>}
    </button>
  );
}
